import React, { useCallback } from 'react';
import { graphql } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';

import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import { EmbeddedConfluenceSource } from './EmbeddedConfluenceSource.tsx';

const eventTypes = [
	'sendOperationalEvent',
	'sendPageEvent',
	'sendTrackEvent',
	'sendUIEvent',
	'sendScreenEvent',
];

type ConfluenceAnalyticsListenerProps = {
	children: React.ReactNode;
};

// Checks if content attributes should be added to this analytics events
// If so, also retrieves the contentId used to fetch those attributes
// out of the GraphQL cache.
// Currently, the only attributes added are `type` and `subType`
//
// Returns [shouldAddContentAttributes: boolean, contentId: string];
const getContentAttributesInfo = (event: UIAnalyticsEvent) => {
	if (event?.payload?.type === 'sendOperationalEvent') {
		// Exclude operational events
		return [false, ''];
	} else if (event?.payload?.data?.attributes?.contentId) {
		// Use the contentId attribute if it exists
		return [true, event?.payload?.data?.attributes?.contentId];
	} else if (event?.payload?.data?.objectType === 'page') {
		// Otherwise, use the objectId if the object is a page
		return [true, event?.payload?.data?.objectId];
	} else {
		// Otherwise, exclude this event because we don't have a contentId for it
		return [false, ''];
	}
};

export const ConfluenceAnalyticsListener = ({ children }: ConfluenceAnalyticsListenerProps) => {
	const apolloClient = useApolloClient();
	const readFragmentInterop = useReadFragmentInterop<any>({
		fragmentSpec: graphql`
			fragment analyticsListener_content on Content {
				subType
			}
		`,
	});

	const convertToAnalyticsAttribute = (value: string | undefined | null): boolean | undefined => {
		if (value === null) {
			// A subType of `null` is used for non-live pages
			return false;
		} else if (value === 'live') {
			return true;
		} else {
			// Otherwise, subType was not found or the page had a subType other than 'live'.
			return undefined;
		}
	};

	const sendAnalyticEvents = useCallback(
		(event: UIAnalyticsEvent) => {
			if (!event || !event.payload) {
				throw new Error('Analytics Web Client Payload is required');
			} else if (!eventTypes.includes(event.payload.type)) {
				throw new Error(`Recieved Unknown Type for Analytics Web Client: ${event.payload.type}`);
			}

			let contentAttributes = {};
			const [shouldAddContentAttributes, contentId] = getContentAttributesInfo(event);

			if (apolloClient && shouldAddContentAttributes) {
				let data;
				try {
					data = readFragmentInterop({
						id: `Content:${contentId}`,
					});
				} catch {}
				contentAttributes = {
					isLivePage: convertToAnalyticsAttribute(data?.subType || data?.singleContent?.subType),
				};
			}
			void getAnalyticsWebClient().then((analyticWebClientResponse) => {
				const newData = {
					...event.payload.data,
					attributes: {
						...contentAttributes,
						...event.payload.data?.attributes,
					},
				};
				analyticWebClientResponse[event.payload.type](newData);
			});
		},
		[apolloClient, readFragmentInterop],
	);

	return (
		<AnalyticsListener onEvent={sendAnalyticEvents}>
			<EmbeddedConfluenceSource>{children}</EmbeddedConfluenceSource>
		</AnalyticsListener>
	);
};
